import { Directive, forwardRef, HostBinding, HostListener, Input } from '@angular/core';
import type { AbstractControl, ControlValueAccessor, ValidatorFn } from '@angular/forms';
import { NG_VALIDATORS, ValidationErrors } from '@angular/forms';
import moment, { Moment } from 'moment';
import { MuiInputBaseDirective } from '../input/mui-input-base.directive';


let nextUniqueId = 0;

export const MUI_TIME_INPUT_FORMAT = 'HH:mm:ssZ';

const parseTime = (time: string): Moment => {
  return moment.parseZone(time, MUI_TIME_INPUT_FORMAT, true);
};

export const muiTimeInputValidator: ValidatorFn = (control: AbstractControl<string>): ValidationErrors | null => {
  if (!control.value) {
    return null;
  }

  const model = parseTime(control.value);

  if (!model.isValid()) {
    return {
      timeFormatError: {
        value: control.value,
        message: `The provided value ${control.value} could not be parsed into a valid time by moment.js.`
      }
    };
  }

  return null;
};

@Directive({
  selector: 'input[muiTimeInput]',
  providers: [
    {
      provide: NG_VALIDATORS,
      useValue: muiTimeInputValidator,
      multi: true
    },
    {
      provide: MuiInputBaseDirective,
      useExisting: forwardRef(() => MuiTimeInputDirective)
    }
  ]
})
export class MuiTimeInputDirective extends MuiInputBaseDirective<string> implements ControlValueAccessor {
  @Input() displayFormat = 'LT';

  @HostBinding('type')
  type = 'text';

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected _propagateChange = (_: string): void => {};

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected _propagateTouched = (): void => {};

  private get _htmlInput(): HTMLInputElement {
    return this._elementRef.nativeElement;
  }

  protected _uid = `mui-time-input-${nextUniqueId++}`;
  private _model: Moment | null = null;

  constructor() {
    super();
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  registerOnChange(fn: (_: string) => void): void {
    this._propagateChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._propagateTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._elementRef.nativeElement.disabled = isDisabled;
  }

  @HostListener('change', ['$event'])
  onChange(e: Event): void {
    const val = (e.target as HTMLInputElement).value;

    // Parse the user input into a moment.js object using the display format and local format as fallback.
    this._model = moment(val, [this.displayFormat, 'LT']);

    if (this._model.isValid()) {
      /* If the user input can be parsed into a valid time, format and display it in the HTML input element accordingly and
       * inform outer components about the change. */
      this._htmlInput.value = this._model.format(this.displayFormat);

      /* Output of the directive model is normalized to UTC rfc3339 string */
      const out = this._model.clone().format(MUI_TIME_INPUT_FORMAT);
      this._propagateChange(out);
    } else {
      /* If user input cannot be parsed, return the input directly. validate() function should set this component to invalid */
      this._propagateChange(val);
      this._htmlInput.setCustomValidity(`The provided value ${val} could not be parsed into a valid time by moment.js.`);
    }
  }

  /**
   * Set the internal model property and render the time in the HTML input element.
   *
   * @param value RFC3339 conform full-time string (e.g. 12:00:00+02:00)
   */
  writeValue(value: string): void {
    /* Parse string to extract hour and minute from it. Format must be RFC3339 conform. */
    const m = parseTime(value);

    /* Normalize time and write to model. */
    this._model = m.set({ seconds: 0, milliseconds: 0, date: 1, month: 1, year: 2020 });

    if (this._model.isValid()) {
      this._htmlInput.value = this._model.format(this.displayFormat);
    } else {
      this._htmlInput.setCustomValidity(`The provided value ${value} could not be parsed into a valid time by moment.js.`);
      this._htmlInput.value = value;
    }
  }

  @HostListener('blur')
  protected onBlur(): void {
    this._propagateTouched();
  }
}
